/* Darlin' — Two Worlds · "The Manifesto"
   Pinned three-line takeover. Call initManifesto() from main.js
   AFTER gsap.registerPlugin(ScrollTrigger) and the body.js flag.
   Day-world physics: snappy power3 hand-offs, back.out CTA landing. */

function initManifesto() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (!window.gsap || !window.ScrollTrigger) return;

  const manifesto = document.querySelector('.manifesto');
  const stage = manifesto?.querySelector('.manifesto-stage');
  const line1 = manifesto?.querySelector('.manifesto-line-1');
  const line2 = manifesto?.querySelector('.manifesto-line-2');
  const line3 = manifesto?.querySelector('.manifesto-line-3');
  const cta = manifesto?.querySelector('.manifesto-cta');
  if (!manifesto || !stage || !line1 || !line2 || !line3 || !cta) return;

  const tl = gsap.timeline({
    scrollTrigger: {
      trigger: manifesto,
      start: 'top top',
      end: '+=250%',
      pin: stage,
      scrub: new URLSearchParams(location.search).has('y') ? true : 0.4,
    },
  });

  tl
    /* line 1 (visible on arrival) snaps up and out */
    .to(line1, {
      autoAlpha: 0, y: -70, scale: 0.97,
      ease: 'power3.in', duration: 0.10,
    }, 0.16)

    /* line 2 takes the stage… */
    .fromTo(line2,
      { autoAlpha: 0, y: 80 },
      { autoAlpha: 1, y: 0, ease: 'power3.out', duration: 0.12 },
      0.24)

    /* …then hands off the same way */
    .to(line2, {
      autoAlpha: 0, y: -70, scale: 0.97,
      ease: 'power3.in', duration: 0.10,
    }, 0.52)

    /* line 3 settles in and stays */
    .fromTo(line3,
      { autoAlpha: 0, y: 80, scale: 0.98 },
      { autoAlpha: 1, y: 0, scale: 1, ease: 'power3.out', duration: 0.12 },
      0.60)

    /* final beat: the CTA lands with a springy overshoot */
    .fromTo(cta,
      { autoAlpha: 0, y: 46, scale: 0.6 },
      { autoAlpha: 1, y: 0, scale: 1, ease: 'back.out(2.2)', duration: 0.14 },
      0.80);
}


/* Darlin' — Two Worlds · sections/doors-deck
   Two initializers, no top-level execution. Call them from main.js
   (after gsap.registerPlugin(ScrollTrigger) or standalone — each guards
   itself). Reduced motion / no-GSAP: early return, CSS shows everything
   statically (hide-for-animation lives under .js selectors only). */

/* ---------- 1 · the two doors — slide in from left / right ---------- */
function initDoors() {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const doors = document.querySelector('.doors');
  const skinDoor = doors?.querySelector('.doors-skin');
  const scentDoor = doors?.querySelector('.doors-scent');
  if (reduced || !window.gsap || !window.ScrollTrigger || !doors || !skinDoor || !scentDoor) return;
  gsap.registerPlugin(ScrollTrigger);

  // day physics: quick, confident, power3.out
  gsap.fromTo(
    skinDoor,
    { xPercent: -36, opacity: 0 },
    {
      xPercent: 0, opacity: 1, duration: 0.9, ease: 'power3.out',
      scrollTrigger: { trigger: doors, start: 'top 75%' },
    }
  );
  gsap.fromTo(
    scentDoor,
    { xPercent: 36, opacity: 0 },
    {
      xPercent: 0, opacity: 1, duration: 0.9, ease: 'power3.out',
      scrollTrigger: { trigger: doors, start: 'top 75%' },
    }
  );
}

/* ---------- 2 · the scent deck — dealt in like a hand of cards ---------- */
function initScentDeck() {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const deck = document.querySelector('.scent-deck');
  const hand = deck?.querySelector('.deck-hand');
  const cards = deck ? Array.from(deck.querySelectorAll('.deck-card')) : [];
  if (reduced || !window.gsap || !window.ScrollTrigger || !deck || !hand || cards.length === 0) return;
  gsap.registerPlugin(ScrollTrigger);

  // plain catalog cards — a quiet rise, no dealing/tilt theatrics
  gsap.fromTo(
    cards,
    {
      opacity: 0,
      y: 32,
    },
    {
      opacity: 1,
      y: 0,
      rotation: 0,
      duration: 0.7,
      ease: 'power2.out',
      stagger: 0.08,
      scrollTrigger: { trigger: hand, start: 'top 80%' },
    }
  );
}


/* Before & After — comparison slider with flick momentum (day world)
   Called once from main.js after the fragment is in the DOM: initBeforeAfter() */

function initBeforeAfter() {
  const section = document.querySelector('.before-after');
  if (!section) return;

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const hasGsap = typeof window.gsap !== 'undefined';

  const compare = section.querySelector('.ba-compare');
  const handle = section.querySelector('.ba-handle');
  const labelBefore = section.querySelector('.ba-label-before');
  const labelAfter = section.querySelector('.ba-label-after');
  const frame = section.querySelector('.ba-frame');
  const fact = section.querySelector('.ba-fact');
  const productAnchor = section.querySelector('.ba-product-anchor');
  if (!compare || !handle || !labelBefore || !labelAfter || !frame) return;

  const MIN = 3;
  const MAX = 97;
  const FRICTION = 0.92;
  const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

  /* ---------- single source of truth for the divider ---------- */
  const state = { pos: 50 };
  let activeTween = null;
  let momentumId = null;
  let vel = 0; // % per frame

  function render() {
    compare.style.setProperty('--ba-pos', state.pos);
    handle.setAttribute('aria-valuenow', String(Math.round(state.pos)));
    // labels fade as the divider crosses their corner
    labelBefore.style.opacity = clamp((state.pos - 6) / 14, 0, 1);
    labelAfter.style.opacity = clamp((94 - state.pos) / 14, 0, 1);
  }

  function stopMotion() {
    if (activeTween) { activeTween.kill(); activeTween = null; }
    if (momentumId) { cancelAnimationFrame(momentumId); momentumId = null; }
  }

  /* smooth position setter — direct set under reduced motion / no gsap */
  function goTo(target, duration) {
    stopMotion();
    const t = clamp(target, MIN, MAX);
    if (reduced || !hasGsap) {
      state.pos = t;
      render();
      return;
    }
    activeTween = gsap.to(state, {
      pos: t,
      duration: duration || 0.7,
      ease: 'power3.out',
      onUpdate: render,
      onComplete: () => { activeTween = null; },
    });
  }

  /* ---------- drag with momentum ---------- */
  let dragging = false;
  let lastT = 0;

  function posFromClientX(clientX) {
    const rect = compare.getBoundingClientRect();
    return clamp(((clientX - rect.left) / rect.width) * 100, MIN, MAX);
  }

  function startMomentum() {
    if (reduced || Math.abs(vel) < 0.15) return;
    const step = () => {
      state.pos = clamp(state.pos + vel, MIN, MAX);
      vel *= FRICTION;
      render();
      if (Math.abs(vel) < 0.02 || state.pos <= MIN || state.pos >= MAX) {
        momentumId = null;
        return;
      }
      momentumId = requestAnimationFrame(step);
    };
    momentumId = requestAnimationFrame(step);
  }

  handle.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    stopMotion();
    dragging = true;
    vel = 0;
    lastT = performance.now();
    handle.setPointerCapture(e.pointerId);
  });

  handle.addEventListener('pointermove', (e) => {
    if (!dragging) return;
    const now = performance.now();
    const dt = Math.max(now - lastT, 1);
    const prev = state.pos;
    state.pos = posFromClientX(e.clientX);
    // instantaneous velocity normalized to %-per-frame (16.7ms), lightly smoothed
    const inst = ((state.pos - prev) / dt) * 16.7;
    vel = vel * 0.4 + inst * 0.6;
    lastT = now;
    render();
  });

  function endDrag(e) {
    if (!dragging) return;
    dragging = false;
    if (handle.hasPointerCapture && handle.hasPointerCapture(e.pointerId)) {
      handle.releasePointerCapture(e.pointerId);
    }
    // flick: velocity carries the handle, friction settles it
    if (performance.now() - lastT < 80) startMomentum();
  }
  handle.addEventListener('pointerup', endDrag);
  handle.addEventListener('pointercancel', endDrag);

  /* ---------- click anywhere on the image → tween the divider there ---------- */
  compare.addEventListener('click', (e) => {
    if (e.target.closest('.ba-handle')) return; // handle clicks are drags, not jumps
    goTo(posFromClientX(e.clientX));
  });

  /* ---------- keyboard (role="slider") ---------- */
  handle.addEventListener('keydown', (e) => {
    let target = null;
    if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') target = state.pos - 5;
    else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') target = state.pos + 5;
    else if (e.key === 'Home') target = MIN;
    else if (e.key === 'End') target = MAX;
    if (target === null) return;
    e.preventDefault();
    goTo(target, 0.45);
  });

  render();

  /* ---------- entrance reveal ---------- */
  if (!reduced && hasGsap && window.ScrollTrigger) {
    gsap.registerPlugin(ScrollTrigger);
    const tl = gsap.timeline({
      scrollTrigger: { trigger: section, start: 'top 78%' },
    });
    const headingTargets = [
      section.querySelector('.eyebrow'),
      section.querySelector('.ba-headline'),
    ].filter(Boolean);
    if (headingTargets.length) {
      tl.fromTo(
        headingTargets,
        { opacity: 0, y: 40 },
        { opacity: 1, y: 0, duration: 0.8, ease: 'power3.out', stagger: 0.08 },
        0
      );
    }
    tl.fromTo(
      frame,
      { clipPath: 'inset(0% 50% 0% 50%)' },
      { clipPath: 'inset(0% 0% 0% 0%)', duration: 1, ease: 'power3.out' },
      0.15
    );
    const factualItems = [fact, productAnchor].filter(Boolean);
    if (factualItems.length) {
      tl.fromTo(
        factualItems,
        { opacity: 0, y: 16 },
        { opacity: 1, y: 0, duration: 0.6, ease: 'power3.out', stagger: 0.06 },
        0.55
      );
    }
  } else {
    // no entrance: make sure nothing stays hidden if body.js is set
    frame.style.clipPath = 'none';
    section.querySelectorAll('.eyebrow, .ba-headline, .ba-fact, .ba-product-anchor').forEach((el) => {
      el.style.opacity = 1;
    });
  }
}


/* Darlin' — Two Worlds · Education + Full-Disclosure Wall
   Two init functions, no top-level execution.
   Call from main.js AFTER body.classList.add('js') and
   gsap.registerPlugin(ScrollTrigger). Both functions are safe to
   call when GSAP is absent or prefers-reduced-motion is on —
   they degrade to the static layout (flips still work, instantly). */

/* ---------- 1 · EDUCATION — day-world stagger reveal ---------- */

function initEducation() {
  const section = document.querySelector('.education');
  if (!section) return;

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const animate =
    !reduced && window.gsap && document.body.classList.contains('js');
  if (!animate) return; // static: CSS leaves everything visible without .js

  const head = section.querySelector('.edu-head');
  const entries = Array.from(section.querySelectorAll('.edu-entry'));
  const entriesWrap = section.querySelector('.edu-entries');
  const wink = section.querySelector('.edu-wink');

  // headline block first…
  if (head) {
    gsap.fromTo(
      head,
      { opacity: 0, y: 50 },
      {
        opacity: 1, y: 0, duration: 0.9, ease: 'power3.out',
        scrollTrigger: { trigger: head, start: 'top 82%' },
      }
    );
  }

  // …then the encyclopedia entries file in, one by one (day physics)
  if (entries.length && entriesWrap) {
    gsap.fromTo(
      entries,
      { opacity: 0, y: 60 },
      {
        opacity: 1, y: 0, duration: 0.85, ease: 'power3.out', stagger: 0.16,
        scrollTrigger: { trigger: entriesWrap, start: 'top 78%' },
      }
    );
  }

  // the wink, last
  if (wink) {
    gsap.fromTo(
      wink,
      { opacity: 0, y: 30 },
      {
        opacity: 1, y: 0, duration: 0.8, ease: 'power3.out',
        scrollTrigger: { trigger: wink, start: 'top 90%' },
      }
    );
  }
}

/* ---------- 2 · DISCLOSURE — flip cards + diagonal 3D entrance ---------- */

function initDisclosure() {
  const section = document.querySelector('.disclosure');
  if (!section) return;

  const cards = Array.from(section.querySelectorAll('.disc-card'));

  /* flip interaction — works with or without GSAP / motion.
     Cards are <button>s, so Enter/Space fire click natively. */
  cards.forEach((card) => {
    card.addEventListener('click', () => {
      const flipped = card.classList.toggle('is-flipped');
      card.setAttribute('aria-pressed', String(flipped));
    });
  });

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const animate =
    !reduced && window.gsap && document.body.classList.contains('js');
  if (!animate) return; // static reveal; CSS flip transition is off too

  // headline glides in — night physics: slower, silkier
  const head = section.querySelector('.disc-head');
  if (head) {
    gsap.fromTo(
      head,
      { opacity: 0, y: 44 },
      {
        opacity: 1, y: 0, duration: 1.4, ease: 'power2.out',
        scrollTrigger: { trigger: head, start: 'top 82%' },
      }
    );
  }

  /* cards flip in from the void, staggered along the grid diagonal.
     Entrance rotates the BUTTON (inline transform), so the click-flip —
     which lives on .disc-inner — never fights it. */
  const grid = section.querySelector('.disc-grid');
  if (!grid || cards.length === 0) return;
  const cols = () => {
    const template = getComputedStyle(grid).gridTemplateColumns;
    return Math.max(1, template.split(' ').length);
  };

  gsap.fromTo(
    cards,
    { opacity: 0, rotateY: -82, y: 30, transformPerspective: 1400 },
    {
      opacity: 1, rotateY: 0, y: 0,
      duration: 1.5,
      ease: 'expo.out',
      stagger: (i) => {
        const c = cols();
        return ((i % c) + Math.floor(i / c)) * 0.13; // diagonal wave
      },
      scrollTrigger: { trigger: grid, start: 'top 78%' },
      onComplete: () => gsap.set(cards, { clearProps: 'transform' }),
    }
  );
}


/* Reviews — word of mouth
   One oversized quote at a time; words roll up and out, next rolls in.
   Auto-advance uses the section setting, paused on hover/focus; dots clickable.
   Reduced motion → instant swap via dots only, no auto, no roll. */

function initReviews() {
  const section = document.querySelector('.reviews');
  if (!section) return;

  const stage = section.querySelector('.rv-stage');
  const slides = Array.from(section.querySelectorAll('.rv-slide'));
  const dots = Array.from(section.querySelectorAll('.rv-dot'));
  if (!stage || slides.length === 0) return;
  if (slides.some((slide) => !slide.querySelector('.rv-quote') || !slide.querySelector('.rv-attr'))) return;

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const configuredHold = Number(stage.dataset.interval);
  const HOLD = Number.isFinite(configuredHold) ? configuredHold : 4.5;

  let current = 0;
  let busy = false;
  let timer = null;

  /* split each quote into masked word spans (JS-built, so no-JS stays clean) */
  slides.forEach((slide) => {
    const quote = slide.querySelector('.rv-quote');
    const words = quote.textContent.trim().split(/\s+/);
    quote.textContent = '';
    slide._words = words.map((word, i) => {
      const mask = document.createElement('span');
      mask.className = 'rv-word-mask';
      const span = document.createElement('span');
      span.className = 'rv-word';
      span.textContent = word;
      mask.appendChild(span);
      quote.appendChild(mask);
      if (i < words.length - 1) quote.appendChild(document.createTextNode(' '));
      return span;
    });
    slide._attr = slide.querySelector('.rv-attr');
  });

  /* switch to rotation mode: slides go absolute, dots appear */
  section.classList.add('rv-on');
  slides[0].classList.add('is-active');

  /* stage height = tallest slide, so the page never jiggles mid-roll */
  function sizeStage() {
    let h = 0;
    slides.forEach((s) => { h = Math.max(h, s.offsetHeight); });
    stage.style.minHeight = h + 'px';
  }
  sizeStage();
  window.addEventListener('resize', sizeStage);
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(sizeStage);

  function setDots(i) {
    dots.forEach((d, j) => d.setAttribute('aria-current', j === i ? 'true' : 'false'));
  }

  /* ---------- reduced motion (or no GSAP): instant swap via dots only ---------- */
  if (reduced || !window.gsap || !window.ScrollTrigger) {
    dots.forEach((dot, i) => {
      dot.addEventListener('click', () => {
        slides[current].classList.remove('is-active');
        slides[i].classList.add('is-active');
        current = i;
        setDots(i);
      });
    });
    return;
  }

  /* ---------- full motion ---------- */

  function queue() {
    if (timer) timer.kill();
    timer = gsap.delayedCall(HOLD, () => go((current + 1) % slides.length));
  }

  function go(next) {
    if (busy || next === current) return;
    busy = true;
    if (timer) timer.kill();

    const out = slides[current];
    const into = slides[next];
    current = next;
    setDots(next);

    gsap.timeline({
      onComplete() {
        busy = false;
        queue();
      },
    })
      /* current words roll up and out */
      .to(out._words, { yPercent: -110, duration: 0.28, ease: 'power2.in', stagger: 0.02 }, 0)
      .to(out._attr, { opacity: 0, y: -10, duration: 0.2, ease: 'power2.in' }, 0.04)
      .add(() => {
        out.classList.remove('is-active');
        into.classList.add('is-active');
      }, 0.28)
      /* next words roll in */
      .fromTo(into._words,
        { yPercent: 110 },
        { yPercent: 0, duration: 0.32, ease: 'power3.out', stagger: 0.022 }, 0.3)
      .fromTo(into._attr,
        { opacity: 0, y: 12 },
        { opacity: 1, y: 0, duration: 0.3, ease: 'power2.out' }, 0.42);
  }

  dots.forEach((dot, i) => dot.addEventListener('click', () => go(i)));

  /* pause auto-advance while hovered or while keyboard focus is inside */
  section.addEventListener('pointerenter', () => { if (timer) timer.pause(); });
  section.addEventListener('pointerleave', () => { if (timer) timer.resume(); });
  section.addEventListener('focusin', () => { if (timer) timer.pause(); });
  section.addEventListener('focusout', () => { if (timer) timer.resume(); });

  /* entrance: section fades up; auto-advance starts once it's on screen */
  gsap.fromTo(
    section,
    { opacity: 0, y: 48 },
    {
      opacity: 1, y: 0, duration: 0.9, ease: 'power3.out',
      scrollTrigger: {
        trigger: section,
        start: 'top 80%',
        once: true,
        onEnter: queue,
      },
    }
  );
}


/* BOTTOM STACK — entrances for .insta-strip + .site-footer.
   Called by main.js (below-the-flip section modules, before
   ScrollTrigger.sort()/refresh()). No top-level execution. */

function initFooter() {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduced || !window.gsap || !window.ScrollTrigger) return;

  const instaRow = document.querySelector('.insta-strip .ig-row');
  const instaTiles = instaRow ? Array.from(instaRow.querySelectorAll('.ig-tile')) : [];
  const footer = document.querySelector('.site-footer');
  const footerSign = footer?.querySelector('.sf-sign');

  /* insta tiles: stagger-rise — slow night physics */
  if (instaRow && instaTiles.length) gsap.fromTo(
    instaTiles,
    { opacity: 0, y: 44 },
    {
      opacity: 1,
      y: 0,
      duration: 1.2,
      ease: 'power2.out',
      stagger: 0.1,
      scrollTrigger: { trigger: instaRow, start: 'top 82%' },
    }
  );

  /* the standing sign: swells on once like a dimmer, then stays lit.
     No flicker — luxury signs don't flicker. */
  if (footer && footerSign) gsap.fromTo(
    footerSign,
    { opacity: 0, scale: 0.985 },
    {
      opacity: 1,
      scale: 1,
      duration: 1.6,
      ease: 'power2.out',
      /* the sign is the last thing on the page — 'top 85%' can be unreachable
         at max scroll, so trigger off the footer itself instead */
      scrollTrigger: { trigger: footer, start: 'top 60%', once: true },
    }
  );
}


/* Darlin' — Two Worlds prototype
   Feel floor (Lenis) + the dusk flip (scrubbed root-token interpolation) */

(function () {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const root = document.documentElement;

  /* Lightweight, PII-free measurement hooks. Shopify still owns commerce
     events; these placement events make homepage CTA intent measurable in GTM. */
  if (!document.documentElement.dataset.marketingTrackingReady) {
    document.documentElement.dataset.marketingTrackingReady = 'true';
    document.addEventListener('click', (event) => {
      const target = event.target.closest('[data-marketing-event]');
      if (!target) return;

      const detail = {
        event: 'darlin_marketing_click',
        action: target.dataset.marketingEvent,
        placement: target.dataset.marketingPlacement || '',
        product: target.dataset.marketingProduct || '',
        label: (target.textContent || '').replace(/\s+/g, ' ').trim(),
        destination: target.getAttribute('href') || '',
      };

      if (Array.isArray(window.dataLayer)) window.dataLayer.push(detail);
      document.dispatchEvent(new CustomEvent('darlin:marketing-click', { detail }));
    });
  }

  if (reduced || !window.gsap) {
    // Static fallback: day page, night section keeps its own colors via CSS.
    return;
  }

  document.body.classList.add('js');
  gsap.registerPlugin(ScrollTrigger);

  /* The homepage cart squeeze changes the available content width on desktop.
     Refresh once at the new layout immediately, then again after Horizon's
     125ms margin transition so the dusk pin never keeps stale start/end values. */
  let drawerRefreshTimer = null;
  const refreshAfterDrawerLayout = () => {
    if (!window.matchMedia('(min-width: 990px)').matches) return;
    requestAnimationFrame(() => requestAnimationFrame(() => ScrollTrigger.refresh()));
    window.clearTimeout(drawerRefreshTimer);
    drawerRefreshTimer = window.setTimeout(() => ScrollTrigger.refresh(), 180);
  };
  document.addEventListener('theme-drawer:open', refreshAfterDrawerLayout);
  document.addEventListener('theme-drawer:close', refreshAfterDrawerLayout);

  /* ---------- sunset frame sequence ---------- */
  function initSunsetSequence(scope) {
    const sequence = scope?.querySelector('.flip-sequence');
    if (!sequence) return null;

    const canvas = sequence.querySelector('.flip-sequence-canvas');
    const data = sequence.querySelector('.flip-sequence-data');
    const context = canvas && canvas.getContext('2d', { alpha: false });
    if (!canvas || !data || !context) return null;

    let urls;
    try {
      urls = JSON.parse(data.textContent);
    } catch (error) {
      console.warn('Darlin sunset sequence could not read its frame list.', error);
      return null;
    }
    if (!Array.isArray(urls) || urls.length === 0) return null;

    const blobPromises = new Array(urls.length);
    const decoded = new Map();
    const inFlight = new Map();
    const maxDecodedFrames = 14;
    let requestedIndex = 0;
    let drawnIndex = -1;

    function getBlob(index) {
      if (!blobPromises[index]) {
        blobPromises[index] = fetch(urls[index], { cache: 'force-cache' }).then((response) => {
          if (!response.ok) throw new Error(`Frame ${index + 1} returned ${response.status}`);
          return response.blob();
        });
      }
      return blobPromises[index];
    }

    function decodeBlob(blob) {
      if ('createImageBitmap' in window) return createImageBitmap(blob);
      return new Promise((resolve, reject) => {
        const image = new Image();
        const objectUrl = URL.createObjectURL(blob);
        image.onload = () => {
          URL.revokeObjectURL(objectUrl);
          resolve(image);
        };
        image.onerror = () => {
          URL.revokeObjectURL(objectUrl);
          reject(new Error('Frame image could not be decoded.'));
        };
        image.src = objectUrl;
      });
    }

    function remember(index, frame) {
      decoded.delete(index);
      decoded.set(index, frame);

      while (decoded.size > maxDecodedFrames) {
        const oldestIndex = decoded.keys().next().value;
        if (oldestIndex === requestedIndex && decoded.size > 1) {
          const currentFrame = decoded.get(oldestIndex);
          decoded.delete(oldestIndex);
          decoded.set(oldestIndex, currentFrame);
          continue;
        }
        const oldestFrame = decoded.get(oldestIndex);
        if (oldestFrame && typeof oldestFrame.close === 'function') oldestFrame.close();
        decoded.delete(oldestIndex);
      }
    }

    function draw(index, frame) {
      context.drawImage(frame, 0, 0, canvas.width, canvas.height);
      drawnIndex = index;
      sequence.classList.add('is-ready');
    }

    function loadFrame(index) {
      if (decoded.has(index)) return Promise.resolve(decoded.get(index));
      if (inFlight.has(index)) return inFlight.get(index);

      const promise = getBlob(index)
        .then(decodeBlob)
        .then((frame) => {
          remember(index, frame);
          inFlight.delete(index);
          return frame;
        })
        .catch((error) => {
          inFlight.delete(index);
          console.warn(`Darlin sunset frame ${index + 1} could not load.`, error);
          return null;
        });

      inFlight.set(index, promise);
      return promise;
    }

    function nearestDecoded(index) {
      let nearest = null;
      let distance = Infinity;
      decoded.forEach((frame, frameIndex) => {
        const nextDistance = Math.abs(frameIndex - index);
        if (nextDistance < distance) {
          nearest = [frameIndex, frame];
          distance = nextDistance;
        }
      });
      return nearest;
    }

    function render(progress) {
      const index = Math.round(gsap.utils.clamp(0, 1, progress) * (urls.length - 1));
      requestedIndex = index;

      if (decoded.has(index)) {
        if (drawnIndex !== index) draw(index, decoded.get(index));
      } else {
        const nearest = nearestDecoded(index);
        if (nearest && drawnIndex !== nearest[0]) draw(nearest[0], nearest[1]);
        loadFrame(index).then((frame) => {
          if (frame && requestedIndex === index) draw(index, frame);
        });
      }

      [index - 2, index - 1, index + 1, index + 2]
        .filter((candidate) => candidate >= 0 && candidate < urls.length)
        .forEach((candidate) => loadFrame(candidate));
    }

    /* The whole sequence is only ~1.8 MB. Warm the browser cache in small
       batches, but keep decoded memory bounded for mobile Safari. */
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    const shouldWarm = !(connection && (connection.saveData || /(^|-)2g$/.test(connection.effectiveType)));
    if (shouldWarm) {
      let warmIndex = 0;
      const warmBatch = () => {
        const batch = [];
        for (let count = 0; count < 6 && warmIndex < urls.length; count += 1, warmIndex += 1) {
          batch.push(getBlob(warmIndex).catch(() => null));
        }
        if (warmIndex < urls.length) Promise.all(batch).then(() => setTimeout(warmBatch, 40));
      };
      if ('requestIdleCallback' in window) requestIdleCallback(warmBatch, { timeout: 800 });
      else setTimeout(warmBatch, 200);
    }

    loadFrame(0).then((frame) => {
      if (frame && requestedIndex === 0) draw(0, frame);
    });

    return { render };
  }

  /* debug mode (?y=<px>): native scroll only, so screenshots are deterministic */
  const DBG = new URLSearchParams(location.search).has('y');

  /* ---------- Lenis smooth scroll, driven by GSAP's ticker ---------- */
  const lenis = DBG ? null : new Lenis({ lerp: 0.11, wheelMultiplier: 1 });
  if (lenis) {
    window.lenis = lenis; // exposed for debugging / scripted scroll
    lenis.on('scroll', ScrollTrigger.update);
    gsap.ticker.add((time) => lenis.raf(time * 1000));
    gsap.ticker.lagSmoothing(0);
  }

  /* ---------- palette scrub: day → dusk → night ---------- */
  const DAY   = { bg: '#E9CDBE', ink: '#141414', disp: '#774312', body: '#5b4a40' };
  const DUSK  = { bg: '#C77E52', ink: '#241210', disp: '#3d2015', body: '#3d2420' };
  const NIGHT = { bg: '#1C0F0D', ink: '#F2D6C7', disp: '#F2D6C7', body: '#D9B9A8' };

  const bgRamp   = gsap.utils.interpolate([DAY.bg, DUSK.bg, NIGHT.bg]);
  const inkRamp  = gsap.utils.interpolate([DAY.ink, DUSK.ink, NIGHT.ink]);
  const dispRamp = gsap.utils.interpolate([DAY.disp, DUSK.disp, NIGHT.disp]);
  const bodyRamp = gsap.utils.interpolate([DAY.body, DUSK.body, NIGHT.body]);

  const world = { t: 0 };
  function paint() {
    root.style.setProperty('--bg', bgRamp(world.t));
    root.style.setProperty('--ink', inkRamp(world.t));
    root.style.setProperty('--display-ink', dispRamp(world.t));
    root.style.setProperty('--body-ink', bodyRamp(world.t));
    root.style.setProperty('--nightness', world.t);
    document.body.dataset.twWorld = world.t >= 0.58 ? 'night' : 'day';
  }
  paint();

  /* ---------- section modules ABOVE the flip (document order matters:
     pinned triggers must be created top-to-bottom so spacers cascade) ---------- */
  if (document.querySelector('.doors') && typeof initDoors === 'function') initDoors();
  if (document.querySelector('.before-after') && typeof initBeforeAfter === 'function') initBeforeAfter();
  if (document.querySelector('.reviews') && typeof initReviews === 'function') initReviews();
  if (document.querySelector('.education') && typeof initEducation === 'function') initEducation();
  if (document.querySelector('.manifesto') && typeof initManifesto === 'function') initManifesto();

  /* ---------- the dusk flip: pinned, scroll-scrubbed ---------- */
  const flip = document.querySelector('#flip');
  const flipStage = flip?.querySelector('.flip-stage');
  let flipScrollTrigger = null;
  if (flip && flipStage) {
    const flipSection = flip.closest('.shopify-section');
    const nextSection = flipSection?.nextElementSibling;
    flipSection?.classList.add('tw-flip-overlay-section');
    if (nextSection?.classList.contains('shopify-section')) {
      nextSection.classList.add('tw-flip-overlay-next');
    }

    const sunsetSequence = initSunsetSequence(flip);
    const sequenceProgress = { t: 0 };
    const ridge = flip.querySelector('.ridge');
    const sun = flip.querySelector('.sun');
    const flipLine = flip.querySelector('.flip-line-a');
    const stars = flip.querySelector('.stars');
    const neonSign = flip.querySelector('.neon-sign');
    // The film already rises in normal document flow. Mount the lockup on that
    // same layer so it rides the handoff instead of staying on the pinned sky.
    if (neonSign && nextSection?.classList.contains('tw-flip-overlay-next')) {
      neonSign.classList.add('tw-flip-overlay-neon');
      nextSection.prepend(neonSign);
    }
    const flipTl = gsap.timeline({
      scrollTrigger: {
        trigger: flip,
        start: 'top top',
        end: () => {
          const mobile = window.matchMedia('(max-width: 640px)').matches;
          const fallback = mobile ? 60 : 110;
          const configured = Number(flipStage.dataset[mobile ? 'flipScrollMobile' : 'flipScrollDesktop']);
          return `+=${Number.isFinite(configured) ? configured : fallback}%`;
        },
        pin: flipStage,
        pinSpacing: false,
        scrub: new URLSearchParams(location.search).has('y') ? true : 0.12,
      },
    });
    flipScrollTrigger = flipTl.scrollTrigger;

    // world re-colors: cream → dusk amber → velvet dark
    flipTl.to(world, { t: 1, duration: 0.9, ease: 'power1.inOut', onUpdate: paint }, 0);

    if (sunsetSequence) {
      flipTl.to(sequenceProgress, {
        t: 1,
        duration: 1,
        ease: 'none',
        onUpdate: () => sunsetSequence.render(sequenceProgress.t),
      }, 0);
      // The real motel silhouette stays grounded while the photographed sun sets behind it.
      if (ridge) flipTl.fromTo(ridge, { yPercent: 8 }, { yPercent: 0, ease: 'power1.out', duration: 0.42 }, 0.08);
    } else {
      if (sun) {
        flipTl.to(sun, { top: '100%', scale: 0.9, ease: 'none', duration: 0.92 }, 0);
        flipTl.to(sun, { backgroundColor: '#E8A13C', ease: 'none', duration: 0.6 }, 0);
      }
      if (ridge) flipTl.fromTo(ridge, { y: 0, yPercent: 105 }, { yPercent: 0, ease: 'power1.in', duration: 0.55 }, 0.08);
    }

    if (flipLine) flipTl.to(flipLine, { opacity: 0, y: -26, duration: 0.12 }, 0.4);
    // full dark: stars out…
    if (stars) flipTl.to(stars, { opacity: 1, duration: 0.22 }, 0.76);
    // …and the neon swells on through the opening quarter of the scrub, so the
    // motel lockup is readable before the mid-sunset handoff.
    if (neonSign) {
      flipTl.fromTo(neonSign,
        { opacity: 0, scale: 0.985 },
        {
          opacity: 1,
          scale: 1,
          ease: 'power2.out',
          duration: 0.48,
        },
        0.14);
    }
  }

  /* ---------- reveals ---------- */
  gsap.utils.toArray('.world-headline, .outro-line').forEach((el) => {
    const waitsForFlipRelease = el.classList.contains('world-headline--post-flip') && flipScrollTrigger;
    gsap.fromTo(
      el,
      { opacity: 0, y: 60 },
      {
        opacity: 1, y: 0, duration: 0.9, ease: 'power3.out',
        scrollTrigger: {
          trigger: el,
          start: waitsForFlipRelease
            ? () => Math.max(
              flipScrollTrigger.end + 1,
              el.getBoundingClientRect().top + window.scrollY - window.innerHeight * 0.82
            )
            : 'top 82%',
        },
      }
    );
  });

  /* cards fade up gently and LAND STRAIGHT — a store shelf, not a scatter */
  const productRow = document.querySelector('.product-row');
  const productCards = productRow ? Array.from(productRow.querySelectorAll('.product-card')) : [];
  if (productRow && productCards.length) {
    productCards.forEach((card, i) => {
      gsap.fromTo(
        card,
        { opacity: 0, y: 24 },
        {
          opacity: 1, y: 0, rotation: 0, duration: 0.6, delay: i * 0.06,
          ease: 'power2.out',
          scrollTrigger: { trigger: productRow, start: 'top 78%' },
        }
      );
    });
  }

  /* night world glides — slower, silkier than the day's bounce */
  const nightFeature = document.querySelector('.night-feature');
  if (nightFeature) {
    gsap.fromTo(
      nightFeature,
      { opacity: 0, y: 48 },
      {
        opacity: 1, y: 0, duration: 1.6, ease: 'power2.out',
        scrollTrigger: { trigger: nightFeature, start: 'top 80%' },
      }
    );
  }

  /* marquee: GSAP-driven, speeds up with scroll velocity */
  const marqueeTrack = document.querySelector('.marquee-track');
  const marqueeTween = DBG || !marqueeTrack ? null : gsap.to(marqueeTrack, {
    xPercent: -50, ease: 'none', duration: 16, repeat: -1,
  });
  if (lenis && marqueeTween) {
    lenis.on('scroll', () => {
      const boost = gsap.utils.clamp(1, 4, 1 + Math.abs(lenis.velocity) / 30);
      marqueeTween.timeScale(boost);
    });
  }

  /* hero product: gentle float */
  const heroProduct = document.querySelector('.hero-product');
  if (!DBG && heroProduct) {
    gsap.to(heroProduct, {
      y: -14, duration: 2.6, ease: 'sine.inOut', yoyo: true, repeat: -1,
    });
  }

  /* ---------- section modules BELOW the flip ---------- */
  if (document.querySelector('.scent-deck') && typeof initScentDeck === 'function') initScentDeck();
  if (document.querySelector('.disclosure') && typeof initDisclosure === 'function') initDisclosure();
  if (typeof initFooter === 'function') initFooter();

  /* all triggers exist — sort by document position and recompute pin spacers */
  ScrollTrigger.sort();
  ScrollTrigger.refresh();

  /* debug: ?y=<px> jumps to a scroll position on load (screenshot verification) */
  if (DBG) {
    const dbgY = parseFloat(new URLSearchParams(location.search).get('y'));
    window.addEventListener('load', () => {
      requestAnimationFrame(() => {
        ScrollTrigger.refresh();
        window.scrollTo(0, dbgY);
        requestAnimationFrame(() => ScrollTrigger.update());
      });
    });
  }

  /* demo cart: add-to-cart buttons bump the count, the jar does a happy bounce */
  const cartBtn = document.querySelector('.cart-btn');
  const cartCount = document.querySelector('.cart-count');
  if (cartBtn && cartCount) {
    document.querySelectorAll('.card-add:not(.tw-atc)').forEach((btn) => {
      btn.addEventListener('click', () => {
        cartCount.textContent = String(parseInt(cartCount.textContent, 10) + 1);
        cartBtn.setAttribute('aria-label', `Cart, ${cartCount.textContent} items`);
        cartBtn.classList.remove('is-adding');
        cartCount.classList.remove('is-adding');
        void cartBtn.offsetWidth; // restart the animation
        cartBtn.classList.add('is-adding');
        cartCount.classList.add('is-adding');
        setTimeout(() => {
          cartBtn.classList.remove('is-adding');
          cartCount.classList.remove('is-adding');
        }, 600);
      });
    });
  }

  /* anchor links routed through Lenis */
  document.querySelectorAll('a[href^="#"]').forEach((a) => {
    a.addEventListener('click', (e) => {
      const href = a.getAttribute('href');
      if (href === '#') return; // placeholder links — let them no-op
      const target = document.querySelector(href);
      if (target && lenis) {
        e.preventDefault();
        lenis.scrollTo(target, { offset: 0 });
      }
    });
  });
})();
